import React from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import ButtonBase from "@mui/material/ButtonBase";
import SkuflixChevronRightIcon from "core/icons/skuflix/SkuflixChevronRightIcon";
import useCarousel from "core/hooks/useCarousel";
import { alpha } from "@mui/system";
import Item from "./Item";

const PREFIX = "ArenaDigitalAssetsCarousel";

const classes = {
  scrollable: `${PREFIX}-scrollable`,
  grid: `${PREFIX}-grid`,
  item: `${PREFIX}-item`,
  prev: `${PREFIX}-prev`,
  next: `${PREFIX}-next`,
  btnRoot: `${PREFIX}-btnRoot`,
  icon: `${PREFIX}-icon`,
};

const Root = styled("div")(({ theme }) => ({
  position: "relative",
  overflow: "hidden",

  [theme.breakpoints.only("xs")]: {
    padding: theme.spacing(0, 2),
  },

  [`& .${classes.scrollable}`]: {
    overflowY: "hidden",
    overflowX: "auto",
    msOverflowStyle: "none", // IE 10+
    scrollbarWidth: "none", // Firefox
    "&::-webkit-scrollbar": {
      display: "none", // Safari and Chrome
    },
    scrollSnapType: "x mandatory",
  },

  [`& .${classes.grid}`]: {
    flexWrap: "nowrap",
  },

  [`& .${classes.item}`]: {
    flexShrink: 0,
    scrollSnapAlign: "start",
  },

  [`& .${classes.prev}`]: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    zIndex: 1,
    width: 0,
    height: "100%",
    pointerEvents: "none",
    "& button": {
      left: 0,
      backgroundImage: `linear-gradient(90deg, ${
        theme.palette.background.body
      }, ${alpha(theme.palette.background.body, 0.6)}, transparent)`,
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  [`& .${classes.next}`]: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    zIndex: 1,
    width: 0,
    height: "100%",
    pointerEvents: "none",
    "& button": {
      right: 0,
      backgroundImage: `linear-gradient(-90deg, ${
        theme.palette.background.body
      }, ${alpha(theme.palette.background.body, 0.6)}, transparent)`,
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  [`& .${classes.btnRoot}`]: {
    pointerEvents: "all",
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 40,
    backgroundColor: "transparent",
    color: "rgba(255, 255, 255, 0.3)",
    "&:hover": {
      color: "#fff",
    },
    [theme.breakpoints.up("sm")]: {
      width: 60,
    },
    [theme.breakpoints.up("md")]: {
      width: 70,
    },
  },

  [`& .${classes.icon}`]: {
    fontSize: 40,
    [theme.breakpoints.up("sm")]: {
      fontSize: 50,
    },
  },
}));

export default function Carousel({
  items,
  sm,
  md,
  lg,
  xl,
  xs,
  variant,
  previewMode,
}) {
  const { rootRef, onScroll, prev, next, onClickPrev, onClickNext } =
    useCarousel(items.length);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <Root>
      <div className={classes.scrollable} ref={rootRef} onScroll={onScroll}>
        <Grid container spacing={3} className={classes.grid}>
          {items.map((item) => (
            <Grid
              item
              container
              xs={xs}
              sm={sm}
              md={md}
              lg={lg}
              xl={xl}
              key={item.id}
              className={classes.item}
            >
              <Item
                variant={variant}
                digitalAsset={item}
                previewMode={previewMode}
                smallTitle={12 / md > 3}
              />
            </Grid>
          ))}
        </Grid>
      </div>

      {prev && (
        <div className={classes.prev}>
          <ButtonBase classes={{ root: classes.btnRoot }} onClick={onClickPrev}>
            <SkuflixChevronRightIcon
              className={classes.icon}
              sx={{ transform: "scaleX(-1)" }}
            />
          </ButtonBase>
        </div>
      )}

      {next && (
        <div className={classes.next}>
          <ButtonBase classes={{ root: classes.btnRoot }} onClick={onClickNext}>
            <SkuflixChevronRightIcon className={classes.icon} />
          </ButtonBase>
        </div>
      )}
    </Root>
  );
}

Carousel.defaultProps = {
  items: [],
};

Carousel.propTypes = {
  variant: PropTypes.oneOf(["TITLE_INSIDE", "TITLE_OUTSIDE"]),
  xs: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  sm: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  md: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  lg: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  xl: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  previewMode: PropTypes.oneOf(["HORIZONTAL", "VERTICAL"]),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      thumbnails: PropTypes.array,
      openInNewWindow: PropTypes.bool,
    })
  ),
};
